import React, { useState } from 'react';
import { useSchemeDocuments, useCreateSchemeDocument, useDeleteSchemeDocument } from '../network/schemes.api';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useToast } from '../../../components/ui/Toast';
import { format } from 'date-fns';

interface SchemeDocumentsTabProps {
  schemeId: string;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  registration_certificate: 'Registration Certificate',
  bylaws: 'Bylaws',
  audit_report: 'Audit Report',
  board_resolution: 'Board Resolution',
  kyc_bundle: 'KYC Bundle',
  insurance_policy: 'Insurance Policy',
  bank_guarantee: 'Bank Guarantee',
  other: 'Other',
};

const CATEGORY_LABELS: Record<string, string> = {
  regulatory: 'Regulatory',
  legal: 'Legal',
  financial: 'Financial',
  operational: 'Operational',
  kyc: 'KYC',
};

export function SchemeDocumentsTab({ schemeId }: SchemeDocumentsTabProps) {
  const [page, setPage] = useState(1);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    documentType: 'other',
    category: 'regulatory',
    fileUrl: '',
    mimeType: '',
    sizeBytes: 0,
    checksum: '',
    effectiveDate: '',
    expiryDate: '',
    isMandatory: false,
    complianceDeadline: '',
    tags: '',
    source: 'internal',
  });
  const { toast } = useToast();

  const { data, isLoading, isError, refetch } = useSchemeDocuments(schemeId, { page, limit: 10 });
  const createMutation = useCreateSchemeDocument(schemeId);
  const deleteMutation = useDeleteSchemeDocument(schemeId);

  const documents = data?.items || [];
  const total = data?.total || 0;

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createMutation.mutateAsync({
        ...uploadForm,
        isMandatory: Boolean(uploadForm.isMandatory),
        tags: uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        effectiveDate: uploadForm.effectiveDate || null,
        expiryDate: uploadForm.expiryDate || null,
        complianceDeadline: uploadForm.complianceDeadline || null,
      });
      toast({ type: 'success', title: 'Document uploaded successfully' });
      setIsUploadOpen(false);
      setUploadForm({
        name: '',
        documentType: 'other',
        category: 'regulatory',
        fileUrl: '',
        mimeType: '',
        sizeBytes: 0,
        checksum: '',
        effectiveDate: '',
        expiryDate: '',
        isMandatory: false,
        complianceDeadline: '',
        tags: '',
        source: 'internal',
      });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Upload failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await deleteMutation.mutateAsync(documentId);
      toast({ type: 'success', title: 'Document deleted' });
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      toast({ type: 'error', title: 'Delete failed', description: err instanceof Error ? err.message : undefined });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadForm(prev => ({
        ...prev,
        name: file.name,
        fileUrl: URL.createObjectURL(file),
        mimeType: file.type,
        sizeBytes: file.size,
        checksum: '', // In real app, compute SHA-256
      }));
    }
  };

  if (isLoading) return <Skeleton rows={6} />;
  if (isError) return (
    <Card className="border-danger-500">
      <CardContent className="p-6 text-center text-danger-600">
        Failed to load documents
        <Button variant="secondary" size="sm" className="ml-3" onClick={() => refetch()}>Retry</Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Scheme Documents</h3>
        <Button onClick={() => setIsUploadOpen(true)}>Upload Document</Button>
      </div>

      <Card className="border border-border shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Version</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Uploaded</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-text-secondary">
                      No documents found. Click "Upload Document" to add one.
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-surface-1">
                      <td className="px-4 py-3 text-sm text-text-primary max-w-xs truncate">{doc.name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-accent-bg text-accent-text">
                          {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-info-bg text-info-text">
                          {CATEGORY_LABELS[doc.category] || doc.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text-secondary">v{doc.version}</td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {doc.createdAt ? format(new Date(doc.createdAt), 'PP') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          doc.status === 'active' ? 'bg-success-bg text-success-text' :
                          doc.status === 'superseded' ? 'bg-warning-bg text-warning-text' :
                          'bg-surface-2 text-text-secondary'
                        }`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-600 hover:underline text-sm"
                          >
                            Download
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-danger-600"
                            onClick={() => handleDelete(doc.id)}
                            disabled={deleteMutation.isPending}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 10 && (
            <div className="flex items-center justify-between px-4 py-3 bg-surface-2 border-t border-border">
              <span className="text-sm text-text-secondary">
                Page {page} of {Math.ceil(total / 10)}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= Math.ceil(total / 10)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadSubmit} className="space-y-4 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Document Name</label>
                <Input value={uploadForm.name} onChange={e => setUploadForm({...uploadForm, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Document Type</label>
                <select
                  className="w-full px-3 py-2 border border-surface-3 rounded-md bg-surface-0 text-text-primary text-sm"
                  value={uploadForm.documentType}
                  onChange={e => setUploadForm({...uploadForm, documentType: e.target.value})}
                >
                  <option value="registration_certificate">Registration Certificate</option>
                  <option value="bylaws">Bylaws</option>
                  <option value="audit_report">Audit Report</option>
                  <option value="board_resolution">Board Resolution</option>
                  <option value="kyc_bundle">KYC Bundle</option>
                  <option value="insurance_policy">Insurance Policy</option>
                  <option value="bank_guarantee">Bank Guarantee</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  className="w-full px-3 py-2 border border-surface-3 rounded-md bg-surface-0 text-text-primary text-sm"
                  value={uploadForm.category}
                  onChange={e => setUploadForm({...uploadForm, category: e.target.value})}
                >
                  <option value="regulatory">Regulatory</option>
                  <option value="legal">Legal</option>
                  <option value="financial">Financial</option>
                  <option value="operational">Operational</option>
                  <option value="kyc">KYC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Source</label>
                <select
                  className="w-full px-3 py-2 border border-surface-3 rounded-md bg-surface-0 text-text-primary text-sm"
                  value={uploadForm.source}
                  onChange={e => setUploadForm({...uploadForm, source: e.target.value})}
                >
                  <option value="internal">Internal</option>
                  <option value="registrar">Registrar</option>
                  <option value="auditor">Auditor</option>
                  <option value="customer">Customer</option>
                  <option value="bank">Bank</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">File</label>
                <Input
                  type="file"
                  className="w-full"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Effective Date</label>
                <Input
                  type="date"
                  value={uploadForm.effectiveDate}
                  onChange={e => setUploadForm({...uploadForm, effectiveDate: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Expiry Date</label>
                <Input
                  type="date"
                  value={uploadForm.expiryDate}
                  onChange={e => setUploadForm({...uploadForm, expiryDate: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Compliance Deadline</label>
                <Input
                  type="date"
                  value={uploadForm.complianceDeadline}
                  onChange={e => setUploadForm({...uploadForm, complianceDeadline: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
              <Input
                value={uploadForm.tags}
                onChange={e => setUploadForm({...uploadForm, tags: e.target.value})}
                placeholder="e.g., registrar, annual-filing"
              />
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="checkbox"
                id="isMandatory"
                checked={uploadForm.isMandatory}
                onChange={e => setUploadForm({...uploadForm, isMandatory: e.target.checked})}
                className="accent-accent-500"
              />
              <label htmlFor="isMandatory" className="text-sm text-text-secondary">Mandatory Document</label>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="secondary" onClick={() => setIsUploadOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Upload Document
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}